import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TablePagination,
} from "@mui/material";
import { BiEdit, BiTrash } from "react-icons/bi";
import toast from "react-hot-toast";

import {
  getAllBranches,
  createBranch,
  deleteBranch,
  updateBranch,
} from "../../Api/branches";

import {
  getAllProjects,
  getProgramsByProject,
} from "../../Api/projectApi";

const Branches = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  /* ===== MODAL ===== */
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  /* ===== FORM ===== */
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [traineeName, setTraineeName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [username, setUsername] = useState("");
  const [project, setProject] = useState("");
  const [program, setProgram] = useState("");

  const [projects, setProjects] = useState([]);
  const [programs, setPrograms] = useState([]);

  /* ===== LOAD DATA ===== */
  const loadBranches = async () => {
    try {
      const res = await getAllBranches();
      if (res.data.success) setData(res.data.data);
    } catch {
      toast.error("Failed to load centers");
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    const res = await getAllProjects();
    const unique = [...new Set(res.data.map(p => p.projectName))];
    setProjects(unique);
  };

  useEffect(() => {
    loadBranches();
  }, []);

  /* ===== PAGINATION ===== */
  const paginated = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* ===== CREATE ===== */
  const openCreateModal = async () => {
    setEditId(null);
    setName("");
    setLocation("");
    setTraineeName("");
    setEmail("");
    setMobile("");
    setUsername("");
    setProject("");
    setProgram("");
    setPrograms([]);

    await loadProjects();
    setOpen(true);
  };

  /* ===== PROJECT CHANGE ===== */
  const handleProjectChange = async (e) => {
    const value = e.target.value;
    setProject(value);
    setProgram("");

    if (value) {
      const res = await getProgramsByProject(value);
      setPrograms(res.data);
    } else {
      setPrograms([]);
    }
  };

  /* ===== EDIT ===== */
  const handleEdit = async (row) => {
    setEditId(row._id);
    setName(row.name);
    setLocation(row.location);
    setTraineeName(row.traineeName);
    setEmail(row.email);
    setMobile(row.mobile);
    setUsername(row.username || "");
    setProject(row.project || "");
    setProgram(row.program || "");

    await loadProjects();

    if (row.project) {
      const res = await getProgramsByProject(row.project);
      setPrograms(res.data);
    }

    setOpen(true);
  };

  /* ===== SAVE ===== */
  const handleSubmit = async () => {
    setSubmitting(true);

    const payload = {
      name,
      location,
      traineeName,
      mobile,
      email,
      username,
      project,
      program,
    };

    try {
      if (editId) {
        await updateBranch(editId, payload);
        toast.success("Center updated");
      } else {
        await createBranch(payload);
        toast.success("Center created");
      }
      setOpen(false);
      loadBranches();
    } catch {
      toast.error("Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  /* ===== DELETE ===== */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this center?")) return;

    try {
      await deleteBranch(id);
      toast.success("Center deleted");
      loadBranches();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Box p={2}>
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="bold">
          Centers
        </Typography>
        <Button variant="contained" onClick={openCreateModal}>
          Create Center
        </Button>
      </Box>

      {/* TABLE */}
      <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow bg-white">
        <table className="min-w-full text-sm">
<thead className="bg-gray-100">
  <tr>
    {[
       "S.No",
  "Center",
  "Location",
  "Project / Program",
  "Coordinator",
  "Contact",
  "Actions",
    ].map((h) => (
      <th key={h} className="px-4 py-3 border-b text-left whitespace-nowrap">
        {h}
      </th>
    ))}
  </tr>
</thead>


          <tbody className="divide-y">
            {paginated.length ? (
              paginated.map((row, index) => (
                <tr key={row._id} className="hover:bg-indigo-50">
                  {/* S.NO */}
                  <td className="px-4 py-3 font-semibold">
                    {page * rowsPerPage + index + 1}
                  </td>

                  {/* CENTER */}
                  <td className="px-4 py-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold">
                      {row.name?.charAt(0)?.toUpperCase()}
                    </div>
                    {row.name}
                  </td>

                  <td className="px-4 py-3">{row.location}</td>
<td className="px-4 py-3">
  {row.project ? (
    <div className="flex flex-col gap-1">
      <span className="inline-block px-3 py-1 rounded-full text-xs bg-blue-100 text-blue-700 font-semibold w-fit">
        {row.project}
      </span>

      {row.program && (
        <span className="text-xs text-gray-600 ml-1">
          {row.program}
        </span>
      )}
    </div>
  ) : (
    "—"
  )}
</td>


                  <td className="px-4 py-3">{row.traineeName}</td>
<td className="px-4 py-3">
  <div className="flex flex-col gap-1">
    <span className="font-semibold text-gray-800">
      {row.mobile || "—"}
    </span>

    {row.email && (
      <span className="text-xs text-gray-600 break-all">
        {row.email}
      </span>
    )}
  </div>
</td>


                  {/* ACTIONS */}
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(row)}
                        className="p-2 bg-green-100 rounded hover:bg-green-200"
                      >
                        <BiEdit className="text-green-700" />
                      </button>
                      <button
                        onClick={() => handleDelete(row._id)}
                        className="p-2 bg-red-100 rounded hover:bg-red-200"
                      >
                        <BiTrash className="text-red-700" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="py-10 text-center text-gray-500">
                  {loading ? "Loading centers..." : "No centers found"}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* PAGINATION */}
        <TablePagination
          component="div"
          count={data.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </div>

      {/* MODAL */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-3xl bg-white rounded-xl shadow">
            <div className="p-4 border-b font-semibold">
              {editId ? "Edit Center" : "Create Center"}
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <input className="input" placeholder="Center Name" value={name} onChange={(e) => setName(e.target.value)} />
              <input className="input" placeholder="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
              <input className="input" placeholder="Coordinator" value={traineeName} onChange={(e) => setTraineeName(e.target.value)} />
              <input className="input" placeholder="Mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} />
              <input className="input" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <input className="input" placeholder="Username" disabled={!!editId} value={username} onChange={(e) => setUsername(e.target.value)} />

              <select className="input" value={project} onChange={handleProjectChange}>
                <option value="">Select Project</option>
                {projects.map((p) => <option key={p}>{p}</option>)}
              </select>

              <select className="input" value={program} onChange={(e) => setProgram(e.target.value)}>
                <option value="">Select Program</option>
                {programs.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t">
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button variant="contained" disabled={submitting} onClick={handleSubmit}>
                {editId ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default Branches;
